import { Components, Theme } from '@mui/material';
import {
  Error, Focused, Primary, Secondary, Disabled,
} from '../color';
import {
  baseSpace,
} from '../spacing';
import {
  label1, label2, label3,
} from '../typography/jscss';
import { buttonClasses } from './types';
import { Corners } from '../corners/tokens';

const borderWidth = 2;

const makeBorderPad = (a: number, b: number, sub = 0) => (
  `${(baseSpace * a) - sub}px ${(baseSpace * b) - sub}px`
);

export const makePadding = (size = 'medium', sub = 0) => {
  switch (size) {
    case 'small': {
      return makeBorderPad(1.5, 4, sub);
    }

    case 'large': {
      return makeBorderPad(3.5, 4, sub);
    }

    case 'extraSmall': {
      return makeBorderPad(1, 3, sub);
    }

    case 'medium':
    default: {
      return makeBorderPad(2.5, 4, sub);
    }
  }
};

export const MuiButton: Components<Theme>['MuiButton'] = {
  defaultProps: {
    disableFocusRipple: true,
  },
  styleOverrides: {
    sizeExtraSmall: {
      minWidth: 40,
      fontSize: label3.fontSize,
      lineHeight: '16px',
      borderRadius: 6,
      letterSpacing: label3.letterSpacing,
    },
    sizeSmall: {
      fontSize: label2.fontSize,
    },
    sizeMedium: {
      fontSize: label2.fontSize,
    },
    sizeLarge: {
      fontSize: label1.fontSize,
      lineHeight: label1.lineHeight,
    },
    root: {
      borderWidth: 0,
      borderRadius: Corners.large,
      boxShadow: 'none',
      minWidth: 64,
      [`&.${buttonClasses.focusVisible}`]: {
        outlineColor: Focused.main,
        outlineStyle: 'solid',
        outlineWidth: 2,
        outlineOffset: 1,
        boxShadow: 'none',
        zIndex: 1,
      },
      '&:hover': {
        boxShadow: 'none',
      },
      [`&.${buttonClasses.disabled}`]: {
        backgroundColor: Disabled.default.main,
        borderColor: Disabled.default.main,
        borderWidth,
      },
      [`&.${buttonClasses.text}.${buttonClasses.disabled}`]: {
        backgroundColor: 'transparent',
      },
    },
    containedInherit: ({ ownerState }) => ({
      padding: makePadding(ownerState.size),
      color: Secondary.defaultInverse.text,
      backgroundColor: Secondary.defaultInverse.main,
      '&:focus-visible': {
        outlineColor: Focused.inverse,
      },
      '&:hover': {
        backgroundColor: Secondary.defaultInverse.hover,
      },
      '&:active': {
        backgroundColor: Secondary.defaultInverse.pressed,
      },
      [`&.${buttonClasses.selected}:not(.${buttonClasses.disabled})`]: {
        backgroundColor: Secondary.surfaceInverse.active,
      },
      [`&.${buttonClasses.disabled}`]: {
        backgroundColor: Disabled.defaultInverse.main,
        color: Disabled.defaultInverse.text,
      },
    }),
    textInherit: {
      color: Secondary.surfaceInverse.text,
      backgroundColor: Secondary.surfaceInverse.main,
      '&:focus-visible': {
        outlineColor: Focused.inverse,
      },
      '&:hover': {
        backgroundColor: Secondary.surfaceInverse.hover,
      },
      '&:active': {
        backgroundColor: Secondary.surfaceInverse.pressed,
      },
      [`&.${buttonClasses.selected}:not(.${buttonClasses.disabled})`]: {
        backgroundColor: Secondary.surfaceInverse.active,
      },
      [`&.${buttonClasses.disabled}`]: {
        backgroundColor: Disabled.surfaceInverse.main,
        color: Disabled.surfaceInverse.text,
      },
    },
    outlinedInherit: {
      borderColor: Secondary.surfaceInverse.border,
      color: Secondary.surfaceInverse.text,
      backgroundColor: Secondary.surfaceInverse.main,
      '&:focus-visible': {
        outlineColor: Focused.inverse,
      },
      '&:hover': {
        backgroundColor: Secondary.surfaceInverse.hover,
      },
      '&:active': {
        backgroundColor: Secondary.surfaceInverse.pressed,
      },
      [`&.${buttonClasses.selected}:not(.${buttonClasses.disabled})`]: {
        backgroundColor: Secondary.surfaceInverse.active,
      },
      [`&.${buttonClasses.disabled}`]: {
        backgroundColor: Disabled.surfaceInverse.main,
        color: Disabled.surfaceInverse.text,
        borderColor: Disabled.surfaceInverse.border,
      },
    },
    containedPrimary: ({ ownerState }) => ({
      padding: makePadding(ownerState.size),
      '&:hover': {
        backgroundColor: Primary.default.hover,
      },
      '&:active': {
        backgroundColor: Primary.default.pressed,
      },
      [`&.${buttonClasses.focusVisible}`]: {
        outlineColor: Focused.main,
      },
      [`&.${buttonClasses.selected}:not(.${buttonClasses.disabled})`]: {
        backgroundColor: Primary.default.active,
      },
    }),
    containedSecondary: ({ ownerState }) => ({
      padding: makePadding(ownerState.size),
      backgroundColor: Secondary.container.main,
      color: Secondary.container.text,
      '&:hover': {
        backgroundColor: Secondary.container.hover,
        boxShadow: 'none',
      },
      '&:active': {
        backgroundColor: Secondary.container.pressed,
      },
      [`&.${buttonClasses.selected}:not(.${buttonClasses.disabled})`]: {
        backgroundColor: Secondary.container.active,
        color: Secondary.container.text,
      },
    }),
    outlinedSecondary: {
      borderColor: Secondary.default.main,
      '&:hover': {
        backgroundColor: Secondary.surface.hover,
        borderColor: Secondary.default.main,
      },
      '&:active': {
        backgroundColor: Secondary.surface.pressed,
      },
      [`&.${buttonClasses.selected}:not(.${buttonClasses.disabled})`]: {
        backgroundColor: Secondary.surface.active,
        color: Secondary.container.text,
      },
      [`&.${buttonClasses.disabled}`]: {
        backgroundColor: 'transparent',
      },
    },
    outlined: ({ ownerState }) => ({
      borderWidth,
      padding: makePadding(ownerState.size, borderWidth),
      '&:hover': {
        borderWidth,
      },
    }),
    containedError: ({ ownerState }) => ({
      padding: makePadding(ownerState.size),
      backgroundColor: Error.default.main,
      '&:hover': {
        backgroundColor: Error.default.hover,
      },
      '&:active': {
        backgroundColor: Error.default.pressed,
      },
      [`&.${buttonClasses.selected}:not(.${buttonClasses.disabled})`]: {
        backgroundColor: Error.default.active,
      },
    }),
    outlinedError: ({ ownerState }) => ({
      borderWidth,
      padding: makePadding(ownerState.size, borderWidth),
      borderColor: Error.default.main,
      '&:hover': {
        borderWidth,
        borderColor: Error.default.main,
        backgroundColor: Error.surface.hover,
      },
      '&:active': {
        backgroundColor: Error.surface.pressed,
      },
      [`&.${buttonClasses.selected}`]: {
        backgroundColor: Error.surface.active,
      },
      [`&.${buttonClasses.disabled}`]: {
        backgroundColor: 'transparent',
      },
    }),
    text: ({ ownerState }) => ({
      padding: makePadding(ownerState.size),
      '&:hover': {
        backgroundColor: Primary.surface.hover,
      },
      '&:active': {
        backgroundColor: Primary.surface.pressed,
      },
    }),
    textPrimary: {
      [`&.${buttonClasses.selected}:not(.${buttonClasses.disabled})`]: {
        color: Primary.default.main,
        backgroundColor: Primary.surface.active,
      },
    },
    textSecondary: {
      '&:hover': {
        backgroundColor: Secondary.surface.hover,
      },
      '&:active': {
        backgroundColor: Secondary.surface.pressed,
      },
      [`&.${buttonClasses.selected}`]: {
        backgroundColor: Secondary.container.selected,
      },
    },
    textError: {
      '&:hover': {
        backgroundColor: Error.surface.hover,
      },
      '&:active': {
        backgroundColor: Error.surface.pressed,
      },
      [`&.${buttonClasses.selected}`]: {
        backgroundColor: Error.surface.active,
      },
    },
  },
};

export const MuiButtonBase: Components<Theme>['MuiButtonBase'] = {
  defaultProps: {
    disableRipple: true,
  },
};
